import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {string | null} */
  #pendingComplementaryProductId = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener('click', this.#handleComplementaryQuickAddClick);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.addEventListener('click', this.#handleInstallmentsClick, true);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener('click', this.#handleComplementaryQuickAddClick);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.removeEventListener('click', this.#handleInstallmentsClick, true);
    this.#quickAddDialog?.removeEventListener('close', this.#clearPendingComplementaryProduct);
  }

  /** @type {HTMLDialogElement | null} */
  get #quickAddDialog() {
    return document.querySelector('#quick-add-dialog dialog');
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and recommendations.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#refreshScrollHints();
    this.#initializeComplementaryProducts();
  };

  /**
   * Closes the cart drawer before the installments dialog opens.
   *
   * The payment-terms element replaces its shadow content when cart totals change,
   * so listen on this stable ancestor instead of binding to a particular CTA instance.
   *
   * @param {MouseEvent} event
   */
  #handleInstallmentsClick = (event) => {
    const path = event.composedPath();
    const ctaIndex = path.findIndex(
      (element) => element instanceof Element && element.id === 'shopify-installments-cta'
    );
    if (ctaIndex === -1) return;

    const ctaRoot = path.slice(ctaIndex + 1).find((element) => element instanceof ShadowRoot);
    if (!ctaRoot?.host.matches('shopify-payment-terms')) return;

    this.#themeDrawer?.close();
  };

  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => {
      this.#updateStickyState();
      this.#refreshScrollHints();
      this.#initializeComplementaryProducts();
    });
  };

  /**
   * Re-measures the drawer's overflow masks after the dialog becomes visible.
   * ScrollHint normally responds to resize and scroll events, but its initial
   * resize can run while the closed dialog has no measurable layout.
   */
  #refreshScrollHints() {
    customElements.whenDefined('scroll-hint').then(() => {
      requestAnimationFrame(() => {
        if (!this.#themeDrawer?.isOpen) return;

        for (const scrollHint of this.#dialog?.querySelectorAll('scroll-hint') ?? []) {
          if (typeof scrollHint.refresh === 'function') scrollHint.refresh();
        }
      });
    });
  }

  /**
   * Remembers when the shared Quick Add modal was opened from a drawer
   * complementary product. The add event itself originates inside that shared
   * modal, so its DOM ancestry no longer identifies the drawer card.
   *
   * @param {MouseEvent} event
   */
  #handleComplementaryQuickAddClick = (event) => {
    const button =
      event.target instanceof Element
        ? event.target.closest('.real-cart-drawer-complementary .quick-add__button--choose')
        : null;
    const productCard = button?.closest('product-card');

    if (!(productCard instanceof HTMLElement)) return;

    this.#pendingComplementaryProductId = productCard.dataset.productId ?? null;
    this.#quickAddDialog?.removeEventListener('close', this.#clearPendingComplementaryProduct);
    this.#quickAddDialog?.addEventListener('close', this.#clearPendingComplementaryProduct, { once: true });
  };

  #clearPendingComplementaryProduct = () => {
    this.#pendingComplementaryProductId = null;
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );
    const shouldRevealAddedComplement = this.#isComplementaryProductAdd(event, sourceModal);

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() => {
            if (shouldRevealAddedComplement && !detail?.didError) {
              this.#clearPendingComplementaryProduct();
              this.#scrollCartItemsToTop();
            }
            this.#updateStickyState();
            this.#refreshScrollHints();
          });

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  /**
   * Returns true only for an add initiated by the complementary-products area,
   * including adds completed in Horizon's shared Quick Add modal.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   * @param {HTMLDialogElement | null} sourceModal
   */
  #isComplementaryProductAdd(event, sourceModal) {
    if (event.action !== 'add' || !(event.target instanceof Element)) return false;
    if (event.target.closest('.real-cart-drawer-complementary')) return true;
    if (!sourceModal || !this.#pendingComplementaryProductId) return false;

    const sourceProductId = event.target.closest('product-form-component')?.getAttribute('data-product-id');
    const matchesPendingProduct = sourceProductId === this.#pendingComplementaryProductId;

    return matchesPendingProduct;
  }

  #scrollCartItemsToTop() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Horizon switches the active scrolling surface with the sticky-summary
    // layout, so reset both possible containers after the cart morph completes.
    for (const scrollContainer of dialog.querySelectorAll('.cart-drawer__content, .cart-drawer__items')) {
      if (scrollContainer instanceof HTMLElement) {
        scrollContainer.scrollTo({ top: 0, behavior: 'instant' });
      }
    }
  }

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #initializeComplementaryProducts = () => {
    const dialog = this.#dialog;

    if (!dialog) return;

    const complementaryProducts = dialog.querySelectorAll('cart-complementary-products');

    complementaryProducts.forEach((component) => {
      if (typeof component.loadRecommendations === 'function') {
        component.loadRecommendations();
      }
    });
  };

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
